import styles from "./sprint.module.css";

import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import routes from "@/routes";

import {
  Flex,
  Typography,
  Spin,
  Card,
  App,
  Button,
  Modal,
  ConfigProvider,
  Dropdown,
} from "antd";

import MainContext from "@/layouts/MainLayout/context";

import PencilIcon from "@/icons/PencilIcon";
import PlusIcon from "@/icons/PlusIcon";
import TrashIcon from "@/icons/TrashIcon";
import InformationIcon from "@/icons/InformationIcon";
import DotsHorizontalIcon from "@/icons/DotsHorizontalIcon";

import SprintModal from "@/components/modals/SprintModal";
import TaskModal from "@/components/modals/TaskModal";
import CopyTaskModal from "@/components/modals/CopyTaskModal";
import ActualLaborCostsModal from "@/components/modals/ActualLaborCostsModal";
import PlannedLaborCostsModal from "@/components/modals/PlannedLaborCostsModal";
import PublishModal from "@/components/modals/PublishModal";
import SprintTasksModal from "@/components/modals/SprintTasksModal";
import SprintInfoModal from "@/components/modals/SprintInfoModal";

import TasksTable from "@/components/tables/TasksTable";
import TaskExportActions from "@/components/ui/TaskExportActions";
import ShowDeletedTasksSwitch from "@/components/inputs/ShowDeletedTasksSwitch";

import Breadcrumbs from "@/components/ui/Breadcrumbs";

import InfoSection from "./InfoSection";

import { getItem, addTasks, deleteItem } from "@/api/sprints";
import { getStatusOptions as getStatuses } from "@/api/sprintStatuses";
import { getUnitsOptions, getEmployeeUnitsOptions } from "@/api/units";
import {
  getTasksOptions as getTasksList,
  deleteItem as deleteTask,
} from "@/api/tasks";
import { getImportanceOptions as getPriorities } from "@/api/importances";
import { getTaskTypeOptions as getTaskTypes } from "@/api/taskTypes";
import { getStatusOptions as getTaskStatuses } from "@/api/statuses";
import { TASK_EXPORT_ENABLED } from "@/constants/tasks";

const SprintsDetail = ({ isAdmins = false }) => {
  const { id, unit_uuid } = useParams();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { config } = useContext(MainContext);
  const sprintNames = config?.entities?.sprint?.name || {
    singular: "Спринт",
    plural: "Спринты",
  };

  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const [sprintStatuses, setSprintStatuses] = useState([]);
  const [units, setUnits] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);

  const [isModalShow, setIsModalShow] = useState(false);

  const [isModalInfoShow, setIsModalInfoShow] = useState(false);

  const readOnly = !(data?.canManage || isAdmins);

  // initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  const fetchData = async () => {
    setIsLoading(true);

    try {
      const [
        data,
        sprintStatuses,
        { units },
        taskStatuses,
        priorities,
        taskTypes,
      ] = await Promise.all([
        getItem(id, isAdmins),
        getStatuses(),
        isAdmins ? getUnitsOptions() : getEmployeeUnitsOptions(),
        getTaskStatuses(),
        getPriorities(),
        getTaskTypes(),
      ]);

      setData(data);
      setUnits(units);
      setSprintStatuses(sprintStatuses.map((i) => ({ ...i, label: i.name })));
      setTaskStatuses(taskStatuses.map((i) => ({ ...i, label: i.name })));
      setPriorities(priorities.map((i) => ({ ...i, label: i.name })));
      setTaskTypes(taskTypes.map((i) => ({ ...i, label: i.name })));

      fetchTasks();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const [tasks, setTasks] = useState([]);
  const [tasksPending, setTasksPending] = useState(false);
  const [selectedTaskUuids, setSelectedTaskUuids] = useState([]);
  const [showDeletedTasks, setShowDeletedTasks] = useState(false);

  useEffect(() => {
    setSelectedTaskUuids((prev) =>
      prev.filter((uuid) => tasks.some((task) => task.uuid === uuid)),
    );
  }, [tasks]);

  const fetchTasks = async (shouldShowDeleted = showDeletedTasks) => {
    setTasksPending(true);
    try {
      const { tasks } = await getTasksList(
        { sprint_uuid: id, include_deleted: shouldShowDeleted },
        isAdmins,
      );
      setTasks(tasks);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setTasksPending(false);
    }
  };

  const handleShowDeletedTasksChange = (checked) => {
    setShowDeletedTasks(checked);
    setSelectedTaskUuids([]);
    fetchTasks(checked);
  };

  // Открыть модалку для редактирования спринта
  const handleEditItem = () => {
    setIsModalShow(true);
  };

  // После сохранения изменений в модалке
  const handleSaveItem = () => {
    setIsModalShow(false);
    fetchData();
  };

  // Удаление спринта
  const handleDelete = () => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить ${data.name}?`,
      content: (
        <span>
          {sprintNames.singular} будет полностью удален из&nbsp;системы.
          Это&nbsp;действие нельзя будет отменить. Прикрепленные задачи
          останутся в&nbsp;системе.
        </span>
      ),
      onOk() {
        handleConfirmDelete(data);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем спринт после подтверждения
  const handleConfirmDelete = async (data) => {
    try {
      await deleteItem({ uuid: data.uuid });

      isAdmins
        ? navigate(routes.administration.sprints)
        : unit_uuid
          ? navigate(routes.unitSprints(unit_uuid))
          : navigate(routes.sprints);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  // Действия с задачами спринта:

  // Окно копирования
  const [copyTask, setCopyTask] = useState(null);
  const [isCopyModalShow, setIsCopyModalShow] = useState(false);

  const handleCopyTask = (item) => {
    setCopyTask(item);
    setIsCopyModalShow(true);
  };

  const handleSaveCopyTask = (txUuid) => {
    setIsCopyModalShow(false);

    const params = new URLSearchParams({
      copy_transaction_id: txUuid,
    }).toString();
    const allTasksPath = isAdmins ? routes.administration.tasks : routes.tasks;

    navigate({ pathname: allTasksPath, search: `?${params}` });
  };

  // Окно редактирования задачи
  const [editTask, setEditTask] = useState(null);
  const [isEditModalShow, setIsEditModalShow] = useState(false);

  const handleEditTask = (item) => {
    setEditTask(item);
    setIsEditModalShow(true);
  };

  const handleEditSave = () => {
    setIsEditModalShow(false);
    fetchTasks();
  };

  // Окно удаления задачи
  const handleDeleteTask = (item) => {
    const currentIsDraft = item?.status?.is_for_draft;

    Modal.confirm({
      title: currentIsDraft
        ? `Вы уверены, что хотите удалить черновик ${item.name}?`
        : `Вы уверены, что хотите удалить задачу ${item.name}?`,
      content: currentIsDraft ? (
        <span>
          Черновик будет полностью удален из&nbsp;системы. Это&nbsp;действие
          нельзя будет отменить. Прикрепленные файлы, ресурсы и&nbsp;подзадачи
          останутся в&nbsp;системе.
        </span>
      ) : (
        "Задача будет переведена в статус 'Удалена'"
      ),
      onOk() {
        handleConfirmDeleteTask(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем задачу после подтверждения
  const handleConfirmDeleteTask = async (item) => {
    setTasksPending(true);
    try {
      await deleteTask({ uuid: item.uuid });
      await fetchTasks();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setTasksPending(false);
    }
  };

  // Окно публикации задачи
  const [publishTask, setPublishTask] = useState(null);
  const [isPublishModalShow, setIsPublishModalShow] = useState(false);

  const handlePublishTask = (item) => {
    setPublishTask(item);
    setIsPublishModalShow(true);
  };

  const handlePublishSave = () => {
    setIsPublishModalShow(false);
    fetchTasks();
  };

  // Окно добавления фактических затрат
  const [actualCostsTask, setActualCostsTask] = useState(null);
  const [isActualCostsModalShow, setIsActualCostsModalShow] = useState(false);

  const handleAddActualCosts = (item) => {
    setActualCostsTask(item);
    setIsActualCostsModalShow(true);
  };

  const handleActualCostsSave = () => {
    setIsActualCostsModalShow(false);
    fetchTasks();
  };

  // Окно добавления планируемых затрат
  const [plannedCostsTask, setPlannedCostsTask] = useState(null);
  const [isPlannedCostsModalShow, setIsPlannedCostsModalShow] = useState(false);

  const handleAddPlannedCosts = (item) => {
    setPlannedCostsTask(item);
    setIsPlannedCostsModalShow(true);
  };

  const handlePlannedCostsSave = () => {
    setIsPlannedCostsModalShow(false);
    fetchTasks();
  };

  // Окно добавления существующих задач в спринт
  const [isAddTasksModalShow, setIsAddTasksModalShow] = useState(false);

  const handleTasksSave = () => {
    setIsAddTasksModalShow(false);
    fetchTasks();
  };

  // Удаление задачи из спринта
  const handleRemoveTaskFromSprint = async (task) => {
    try {
      const item = {
        uuid: data.uuid,
        taskUuids:
          [
            ...tasks
              .filter((item) => item.uuid !== task.uuid)
              .map((item) => item.uuid),
          ] || [],
      };

      await addTasks(item);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      fetchTasks();
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        data && (
          <>
            <Flex vertical gap={25}>
              <Flex vertical gap={10}>
                {/* Хлебные крошки */}
                <Breadcrumbs
                  items={
                    isAdmins
                      ? [
                          { title: "Административная панель" },

                          {
                            title: sprintNames.plural,
                            href: routes.administration.sprints,
                          },

                          {
                            title: data.name,
                          },
                        ]
                      : [
                          {
                            title: "Главная",
                            href: routes.dashboards,
                          },
                          unit_uuid ? { title: data?.unit?.name } : {},

                          {
                            title: sprintNames.plural,
                            href: unit_uuid
                              ? routes.unitSprints(data.unit_uuid)
                              : routes.sprints,
                          },

                          {
                            title: data.name,
                          },
                        ]
                  }
                />

                {/* Заголовок и кнопки */}
                <Flex align="flex-start" gap={20} justify="space-between">
                  <Typography.Title level={2} className="no-margin">
                    {data.name}
                  </Typography.Title>

                  <Flex gap={8} align="center">
                    {!readOnly && (
                      <>
                        <Button
                          title="Редактировать"
                          variant="text"
                          type="text"
                          icon={<PencilIcon />}
                          onClick={handleEditItem}
                        />

                        <div className="hide-desktop">
                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: "addTask",
                                  type: "group",
                                  label: "Добавить задачу",
                                  children: [
                                    {
                                      key: "createNew",
                                      label: "Создать новую",
                                      onClick: () =>
                                        handleEditTask({
                                          sprint_uuid: data.uuid,
                                          unit_uuid: data.unit_uuid,
                                        }),
                                    },
                                    {
                                      key: "addExisting",
                                      label: "Добавить существующую",
                                      onClick: () =>
                                        setIsAddTasksModalShow(true),
                                    },
                                  ],
                                },
                                {
                                  type: "divider",
                                },
                                {
                                  key: "delete",
                                  label: "Удалить",
                                  onClick: handleDelete,
                                },
                              ],
                            }}
                            trigger={["click"]}
                            placement="bottomRight"
                          >
                            <Button
                              color="default"
                              variant="text"
                              type="text"
                              icon={<DotsHorizontalIcon />}
                            />
                          </Dropdown>
                        </div>
                      </>
                    )}

                    <TaskExportActions
                      selectedTasks={selectedTaskUuids}
                      onClear={() => setSelectedTaskUuids([])}
                      isAdmins={isAdmins}
                    />

                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: "#E66A25",
                        },
                      }}
                    >
                      <Button
                        type="primary"
                        icon={<InformationIcon />}
                        onClick={() => setIsModalInfoShow(true)}
                        className="hide-desktop"
                      />
                    </ConfigProvider>
                  </Flex>
                </Flex>
              </Flex>

              <Flex gap={20}>
                <Flex vertical gap={12} className={styles.body}>
                  <ShowDeletedTasksSwitch
                    checked={showDeletedTasks}
                    onChange={handleShowDeletedTasksChange}
                    disabled={tasksPending}
                  />

                  <TasksTable
                    data={tasks}
                    loading={tasksPending}
                    isAdmins={isAdmins}
                    unit_uuid={unit_uuid}
                    pagination={false}
                    statuses={taskStatuses}
                    priorities={priorities}
                    taskTypes={taskTypes}
                    onCopyItem={handleCopyTask}
                    onEditItem={handleEditTask}
                    onDeleteItem={handleDeleteTask}
                    onPublishItem={handlePublishTask}
                    onAddActualCosts={handleAddActualCosts}
                    onEditPlannedCosts={handleAddPlannedCosts}
                    onRemoveTaskFromSprint={
                      !readOnly ? (v) => handleRemoveTaskFromSprint(v) : null
                    }
                    resize
                    selectedRowKeys={
                      TASK_EXPORT_ENABLED ? selectedTaskUuids : undefined
                    }
                    onRowsSelect={
                      TASK_EXPORT_ENABLED ? setSelectedTaskUuids : undefined
                    }
                  />
                </Flex>

                <div className={styles.aside}>
                  <Flex
                    vertical
                    gap={10}
                    style={{ position: "sticky", top: 0 }}
                  >
                    <Card>
                      <InfoSection data={data} />
                    </Card>

                    {!readOnly && (
                      <Card>
                        <Flex justify="space-between" align="center">
                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: "createNew",
                                  onClick: () =>
                                    handleEditTask({
                                      sprint_uuid: data.uuid,
                                      unit_uuid: data.unit_uuid,
                                    }),
                                  label: "Создать новую",
                                },
                                {
                                  key: "addExisting",
                                  onClick: () => setIsAddTasksModalShow(true),
                                  label: "Добавить существующую",
                                },
                              ],
                            }}
                            trigger={["click"]}
                            placement="bottomLeft"
                          >
                            <Button
                              type="primary"
                              icon={<PlusIcon style={{ fontSize: 14 }} />}
                            >
                              Добавить задачу
                            </Button>
                          </Dropdown>

                          <Button
                            onClick={handleDelete}
                            icon={<TrashIcon style={{ fontSize: 14 }} />}
                          ></Button>
                        </Flex>
                      </Card>
                    )}
                  </Flex>
                </div>
              </Flex>
            </Flex>

            {isModalShow && (
              <SprintModal
                isAdmins={isAdmins}
                data={data}
                statuses={sprintStatuses}
                visible={isModalShow}
                onSave={handleSaveItem}
                onCancel={() => setIsModalShow(false)}
                unit_uuid={unit_uuid}
                units={units}
              />
            )}

            {isEditModalShow && (
              <TaskModal
                isAdmins={isAdmins}
                data={editTask}
                statuses={taskStatuses}
                priorities={priorities}
                taskTypes={taskTypes}
                units={units}
                visible={isEditModalShow}
                onSave={handleEditSave}
                onCancel={() => setIsEditModalShow(false)}
                unit_uuid={unit_uuid}
              />
            )}

            {isCopyModalShow && (
              <CopyTaskModal
                visible={isCopyModalShow}
                onCancel={() => setIsCopyModalShow(false)}
                onSave={handleSaveCopyTask}
                task={copyTask}
                units={units}
                isAdmins={isAdmins}
                unit_uuid={unit_uuid}
              />
            )}

            {isPublishModalShow && (
              <PublishModal
                data={publishTask}
                statuses={taskStatuses}
                visible={isPublishModalShow}
                onSave={handlePublishSave}
                onCancel={() => setIsPublishModalShow(false)}
              />
            )}

            {isActualCostsModalShow && (
              <ActualLaborCostsModal
                data={null}
                task_uuid={actualCostsTask?.uuid}
                unit_uuid={actualCostsTask?.unit_uuid}
                visible={isActualCostsModalShow}
                onCancel={() => setIsActualCostsModalShow(false)}
                onSave={handleActualCostsSave}
                isAdmins={isAdmins}
              />
            )}

            {isPlannedCostsModalShow && (
              <PlannedLaborCostsModal
                data={plannedCostsTask}
                visible={isPlannedCostsModalShow}
                parentDoNotExceedChildren={
                  plannedCostsTask?.parent?.do_not_exceed_children || false
                }
                plannedUpLimit={
                  plannedCostsTask?.uuid
                    ? (plannedCostsTask?.parent?.available_limit_for_children ??
                        null) + plannedCostsTask?.planned_costs
                    : (plannedCostsTask?.parent?.available_limit_for_children ??
                      null)
                }
                plannedLowLimit={plannedCostsTask?.sum_children_planned_costs}
                onCancel={() => setIsPlannedCostsModalShow(false)}
                onSave={handlePlannedCostsSave}
              />
            )}

            {isAddTasksModalShow && (
              <SprintTasksModal
                visible={isAddTasksModalShow}
                sprint={data}
                onSave={handleTasksSave}
                onCancel={() => setIsAddTasksModalShow(false)}
                isAdmins={isAdmins}
              />
            )}

            {isModalInfoShow && (
              <SprintInfoModal
                data={data}
                visible={isModalInfoShow}
                readOnly={readOnly}
                onOk={() => {
                  setIsModalInfoShow(false);
                  handleEditItem();
                }}
                onCancel={() => setIsModalInfoShow(false)}
              />
            )}
          </>
        )
      )}
    </>
  );
};

export default SprintsDetail;
